import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllEdges,
  selectAllNodes,
  selectEditorMoveNode,
} from '../slice/mapSelectors';
import { fromLonLat, toLonLat } from 'ol/proj';
import { MapBrowserEvent } from 'ol';
import { Edge, Node } from '@/types';
import BaseEvent from 'ol/events/Event';
import { selectNodeSize } from '../../plotter/plotterSelectors';
import { mapActions } from '../slice/mapSlice';
import { useUpdateNodeMutation } from '../../services/edgeApi';
import { useMapContext, MapEventListener } from '../context/MapContext';

export const NodePositioningOverlay = () => {
  const dispatch = useDispatch();

  const { map, registerClickListener, unregisterClickListener } =
    useMapContext();

  const [updateNode] = useUpdateNodeMutation();

  const nodeCanvasRef = useRef<HTMLCanvasElement>();

  const nodes = useSelector(selectAllNodes);
  const edges = useSelector(selectAllEdges);
  const editorMoveNode = useSelector(selectEditorMoveNode);
  const nodeSize = useSelector(selectNodeSize);

  useEffect(() => {
    const clickListener: MapEventListener = async (_feature, coordinate) => {
      if (!editorMoveNode) return;
      const node = nodes[editorMoveNode];
      if (!node) {
        dispatch(mapActions.setEditorMoveNode(null));
        return;
      }
      const transformedCoordinate = toLonLat(coordinate);
      const patch = {
        lat: transformedCoordinate[1].toString(),
        lng: transformedCoordinate[0].toString(),
      };
      await updateNode({
        id: node.id,
        patch,
      });
      dispatch(mapActions.setEditorMoveNode(null));
    };
    registerClickListener(clickListener);
    return () => {
      unregisterClickListener(clickListener);
    };
  }, [registerClickListener, unregisterClickListener, editorMoveNode, nodes]);

  useEffect(() => {
    if (!map) return;
    const updateNode = (event: BaseEvent | Event) => {
      const { coordinate } = event as unknown as MapBrowserEvent<PointerEvent>;
      if (!nodeCanvasRef.current) return;
      const ctx = nodeCanvasRef.current.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(
        0,
        0,
        nodeCanvasRef.current.width,
        nodeCanvasRef.current.height,
      );
      if (editorMoveNode) {
        const nodeStartEdges = nodes[editorMoveNode].starts_edges
          .map((edgeId) => edges[edgeId] as Edge)
          .filter(Boolean);
        const nodeEndEdges = nodes[editorMoveNode].ends_edges
          .map((edgeId) => edges[edgeId] as Edge)
          .filter(Boolean);
        const coordinateScreenPos = map.getPixelFromCoordinate(coordinate);

        const drawEdge = (otherNode: Node) => {
          const edgeStartScreenPos = map.getPixelFromCoordinate(
            fromLonLat([Number(otherNode.lng), Number(otherNode.lat)]),
          );
          ctx.beginPath();
          ctx.moveTo(edgeStartScreenPos[0], edgeStartScreenPos[1]);
          ctx.lineTo(coordinateScreenPos[0], coordinateScreenPos[1]);
          ctx.lineWidth = 5;
          ctx.strokeStyle = 'red';
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();
        };
        nodeStartEdges.forEach((edge) => {
          const endNode = nodes[edge.end_node];
          if (endNode) {
            drawEdge(endNode);
          }
        });
        nodeEndEdges.forEach((edge) => {
          const startNode = nodes[edge.start_node];
          if (startNode) {
            drawEdge(startNode);
          }
        });
        ctx.beginPath();
        ctx.arc(
          coordinateScreenPos[0],
          coordinateScreenPos[1],
          nodeSize,
          0,
          2 * Math.PI,
        );
        ctx.fillStyle = 'red';
        ctx.fill();
        ctx.stroke();
      } else {
        ctx.clearRect(
          0,
          0,
          nodeCanvasRef.current.width,
          nodeCanvasRef.current.height,
        );
      }
    };
    map.on(['pointermove', 'change'], updateNode);
    return () => {
      map.un(['pointermove', 'change'], updateNode);
    };
  }, [map, editorMoveNode, nodeCanvasRef]);

  if (!map) {
    return <></>;
  }

  return (
    <canvas
      ref={nodeCanvasRef as React.RefObject<HTMLCanvasElement>}
      width={map.getTargetElement().offsetWidth - 6}
      height={map.getTargetElement().offsetHeight - 8}
      style={{
        position: 'absolute',
        top: map.getTargetElement().offsetTop,
        left: map.getTargetElement().offsetLeft,
        pointerEvents: 'none',
        zIndex: 1000,
        overflow: 'hidden',
      }}
    />
  );
};
